import { useState, useEffect } from 'react';

export const loadGoogleFonts = (consent) => {
  if (consent && consent.analytics) {
    if (document.getElementById('google-fonts-link')) return;

    const preconnect1 = document.createElement('link');
    preconnect1.rel = 'preconnect';
    preconnect1.href = 'https://fonts.googleapis.com';

    const preconnect2 = document.createElement('link');
    preconnect2.rel = 'preconnect';
    preconnect2.href = 'https://fonts.gstatic.com';
    preconnect2.crossOrigin = 'anonymous';

    const fontLink = document.createElement('link');
    fontLink.id = 'google-fonts-link';
    fontLink.rel = 'stylesheet';
    fontLink.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&family=Noto+Serif:ital,wght@0,400;0,700;1,400&display=swap';

    const symbolsLink = document.createElement('link');
    symbolsLink.id = 'material-symbols-link';
    symbolsLink.rel = 'stylesheet';
    symbolsLink.href = 'https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:wght,FILL@100..700,0..1&display=swap';

    document.head.appendChild(preconnect1);
    document.head.appendChild(preconnect2);
    document.head.appendChild(fontLink);
    document.head.appendChild(symbolsLink);
  }
};

export const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showPersonalize, setShowPersonalize] = useState(false);
  const [consent, setConsent] = useState({
    essential: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const savedConsent = localStorage.getItem('cookie_consent');
    if (savedConsent) {
      try {
        const parsed = JSON.parse(savedConsent);
        loadGoogleFonts(parsed);
      } catch {
        setIsVisible(true);
      }
    } else {
      setIsVisible(true);
    }
  }, []);

  const handleAcceptEssential = () => {
    const newConsent = { essential: true, analytics: false, marketing: false };
    localStorage.setItem('cookie_consent', JSON.stringify(newConsent));
    loadGoogleFonts(newConsent);
    setIsVisible(false);
  };

  const handleAcceptAll = () => {
    const newConsent = { essential: true, analytics: true, marketing: true };
    localStorage.setItem('cookie_consent', JSON.stringify(newConsent));
    loadGoogleFonts(newConsent);
    setIsVisible(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem('cookie_consent', JSON.stringify(consent));
    loadGoogleFonts(consent);
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 md:max-w-2xl md:left-auto md:right-4 shadow-2xl rounded-2xl glass-panel border border-outline-variant/30 p-6 flex flex-col gap-4 text-on-surface antialiased transition-all duration-300">
      <div className="flex items-start gap-4">
        <span className="material-symbols-outlined text-primary text-3xl hidden sm:block">cookie</span>
        <div className="flex-1 space-y-1.5">
          <h3 className="font-headline text-lg font-bold">Respeitamos sua Privacidade</h3>
          <p className="font-body text-xs text-on-surface-variant/80 leading-relaxed">
            Utilizamos cookies essenciais para o funcionamento correto do nosso site e cookies opcionais para análise estatística de desempenho e personalização da sua experiência de fé, em estrita conformidade com a LGPD.
          </p>
        </div>
      </div>

      {!showPersonalize ? (
        <div className="flex flex-wrap gap-2.5 justify-end mt-2">
          <button
            onClick={() => setShowPersonalize(true)}
            className="px-4 py-2 rounded-full text-xs font-medium text-on-surface-variant hover:text-primary hover:bg-primary/10 transition-colors"
          >
            Personalizar
          </button>
          <button
            onClick={handleAcceptEssential}
            className="px-4 py-2 rounded-full text-xs font-medium bg-surface-container border border-outline-variant/30 text-on-surface hover:bg-surface-container-high transition-colors"
          >
            Apenas Essenciais
          </button>
          <button
            onClick={handleAcceptAll}
            className="px-5 py-2 rounded-full text-xs font-semibold bg-primary text-on-primary hover:opacity-95 shadow-md divine-glow-hover transition-colors"
          >
            Aceitar Todos
          </button>
        </div>
      ) : (
        <div className="space-y-4 border-t border-outline-variant/10 pt-4">
          <div className="space-y-3">
            <label className="flex items-start gap-3 p-3 rounded-lg bg-surface-container-lowest/50 border border-outline-variant/10 cursor-not-allowed">
              <input
                type="checkbox"
                checked
                disabled
                className="mt-0.5 h-4 w-4 rounded border-outline-variant text-primary focus:ring-primary accent-primary cursor-not-allowed opacity-60"
              />
              <div className="space-y-0.5">
                <span className="text-xs font-semibold block">Essenciais (Obrigatório)</span>
                <span className="text-[10px] text-on-surface-variant/60">Necessários para a sessão de usuário, segurança e conformidade do sistema.</span>
              </div>
            </label>

            <label className="flex items-start gap-3 p-3 rounded-lg bg-surface-container-lowest/50 border border-outline-variant/10 cursor-pointer hover:bg-surface-container-low transition-colors">
              <input
                type="checkbox"
                checked={consent.analytics}
                onChange={(e) => setConsent(c => ({ ...c, analytics: e.target.checked }))}
                className="mt-0.5 h-4 w-4 rounded border-outline-variant text-primary focus:ring-primary accent-primary cursor-pointer"
              />
              <div className="space-y-0.5">
                <span className="text-xs font-semibold block">Estatísticas e Performance</span>
                <span className="text-[10px] text-on-surface-variant/60">Permite monitorar o desempenho técnico, carregamento rápido de fontes personalizadas e telemetria.</span>
              </div>
            </label>

            <label className="flex items-start gap-3 p-3 rounded-lg bg-surface-container-lowest/50 border border-outline-variant/10 cursor-pointer hover:bg-surface-container-low transition-colors">
              <input
                type="checkbox"
                checked={consent.marketing}
                onChange={(e) => setConsent(c => ({ ...c, marketing: e.target.checked }))}
                className="mt-0.5 h-4 w-4 rounded border-outline-variant text-primary focus:ring-primary accent-primary cursor-pointer"
              />
              <div className="space-y-0.5">
                <span className="text-xs font-semibold block">Marketing e Personalização</span>
                <span className="text-[10px] text-on-surface-variant/60">Usado para otimizar comunicações diretas e acompanhar a eficácia de novas funcionalidades e anúncios.</span>
              </div>
            </label>
          </div>

          <div className="flex justify-between items-center mt-4">
            <button
              onClick={() => setShowPersonalize(false)}
              className="px-4 py-2 rounded-full text-xs font-medium text-on-surface-variant hover:text-primary transition-colors"
            >
              Voltar
            </button>
            <button
              onClick={handleSavePreferences}
              className="px-5 py-2 rounded-full text-xs font-semibold bg-primary text-on-primary hover:opacity-95 shadow-md divine-glow-hover transition-colors"
            >
              Salvar Preferências
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
